import { useSuspenseQuery } from "@apollo/client";
import { Box, Stack, Typography } from "@mui/material";
import { teal } from "@mui/material/colors";
import { BarChart } from "components/Dashboard/BarChart";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { gql } from "generated-graphql";
import { FacilityWasteView } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatAmountInPounds } from "util/formatNumber";

import { TileContentGreyBackground } from "../TileContentGreyBackground";

// Per month, one bar = GREATEST of that month's per-source totals (vendor /
// e-Manifest / reviewed-document) — the same per-source rule getWasteStreams
// uses for the stream Amount. Bars are a per-month trend, NOT a partition of
// the stream Amount: summing them over-counts whenever different sources peak
// in different months (max-of-monthly-sums ≥ max-over-range). So the summary
// shows the stream's canonical Amount (maxAmount) rather than the sum of bars.
const WASTE_STREAM_SHIPMENTS_BY_MONTH = gql(`
  query WasteStreamShipmentsByMonth($wasteStreamId: ID!, $filter: FacilityWasteShipmentsFilter!) {
    wasteStreamShipmentsByMonth(wasteStreamId: $wasteStreamId, filter: $filter) {
      wasteShipmentsByMonth {
        month
        amount
      }
    }
  }
`);

const AMOUNT_COLOR = teal[300];

// Below this many pixels of horizontal room per bar, the on-bar weight labels
// start colliding (many months and/or a narrow drawer), so we hide them — the
// values are still available on hover.
const MIN_PX_PER_BAR_FOR_LABELS = 52;

// A `type` (not `interface`) so it keeps an implicit index signature and stays
// assignable to MUI x-charts' `DatasetType` when passed as the chart `dataset`.
export type MonthlyShipmentPoint = {
  /** x-axis category + tick label. */
  month: string;
  /** Tooltip title (always carries the year). */
  monthYear: string;
  amount: number;
};

/**
 * Shapes the resolver's monthly buckets into the chart's dataset.
 *
 * The x-axis is a band scale keyed on `month`, so the label MUST be unique per
 * bucket — otherwise a range spanning more than one calendar year produces
 * duplicate month names (two "May"s), the band scale collapses them into a
 * single category, and the hover tooltip maps to the wrong row (wrong year, and
 * a zero amount that formats to an empty string so the weight disappears). When
 * the range crosses years we include the 2-digit year in the label to keep each
 * category distinct; single-year ranges keep the cleaner bare-month label.
 */
export const toMonthlyShipmentChartData = (
  months: ReadonlyArray<{ month: string; amount?: number | null }> | null | undefined
): MonthlyShipmentPoint[] => {
  if (!months) return [];
  const years = new Set(
    months.map((m) => DateTime.fromISO(m.month).year).filter((year) => !Number.isNaN(year))
  );
  const multiYear = years.size > 1;
  return months.map((m) => {
    const date = DateTime.fromISO(m.month);
    return {
      month: date.isValid ? date.toFormat(multiYear ? "MMM yy" : "MMM") : m.month,
      monthYear: date.isValid ? date.toFormat("MMM yyyy") : m.month,
      amount: m.amount ?? 0,
    };
  });
};

// One summary figure shown in the strip beneath the bars.
const Stat = ({ label, value }: { label: string; value: string }) => (
  <Stack spacing={0}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

interface WasteStreamShipmentsByMonthProps {
  wasteStreamId: string;
  startDate: string;
  endDate: string;
  /**
   * The stream's canonical Amount for this range/view (maxAmount from
   * getWasteStreams), shown as the summary's headline figure so it agrees with
   * the Amount elsewhere in the drawer rather than the (over-counting) sum of
   * monthly bars.
   */
  maxAmount: number;
  /** Match the drawer's view/tags so the bars are scoped like maxAmount. */
  view: FacilityWasteView;
  tags: string[];
}

export const WasteStreamShipmentsByMonth = ({
  wasteStreamId,
  startDate,
  endDate,
  maxAmount,
  view,
  tags,
}: WasteStreamShipmentsByMonthProps) => {
  const start = startDate ? DateTime.fromISO(startDate) : null;
  const end = endDate ? DateTime.fromISO(endDate) : null;

  const { data } = useSuspenseQuery(WASTE_STREAM_SHIPMENTS_BY_MONTH, {
    variables: {
      wasteStreamId,
      filter: {
        startDate: start?.toJSDate(),
        endDate: end?.toJSDate(),
        view,
        tags,
      },
    },
  });

  const chartData = useMemo(
    () => toMonthlyShipmentChartData(data?.wasteStreamShipmentsByMonth?.wasteShipmentsByMonth),
    [data]
  );

  // Summary of the bars above — how many months had shipments, the biggest
  // month, and the average bar across months that shipped. The headline Amount
  // is maxAmount (passed in), not the sum of bars: summing per-month maxima
  // over-counts the stream's actual Amount.
  const summary = useMemo(() => {
    const barTotal = chartData.reduce((sum, d) => sum + d.amount, 0);
    const active = chartData.filter((d) => d.amount > 0);
    const peak = active.reduce<(typeof chartData)[number] | null>(
      (best, d) => (best == null || d.amount > best.amount ? d : best),
      null
    );
    return {
      activeCount: active.length,
      peak,
      // Average bar height across months that shipped (a per-month statistic,
      // not a partition of the Amount).
      avg: active.length > 0 ? barTotal / active.length : 0,
    };
  }, [chartData]);

  // Track the chart's rendered width so we can drop the on-bar labels when the
  // bars get too narrow to fit them.
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState(0);
  useEffect(() => {
    const node = chartRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      setChartWidth(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const showBarLabels =
    chartData.length > 0 &&
    (chartWidth === 0 || chartWidth / chartData.length >= MIN_PX_PER_BAR_FOR_LABELS);

  // Drive the empty state off the chart's own data so it can never disagree with
  // what the bars would render. `chartData` always has one entry per month in
  // range (the resolver fills gaps), so "no shipments" means every month's
  // reconciled amount is zero rather than an empty array.
  const hasShipments = chartData.some((d) => d.amount > 0);
  if (!hasShipments) {
    return <TileContentGreyBackground>No shipment data to show</TileContentGreyBackground>;
  }

  return (
    <Stack sx={{ width: "100%" }}>
      <DashboardTile.ContentArea>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Waste stream shipments (lbs)
        </Typography>
        <Box ref={chartRef} sx={{ height: 200, width: "100%", color: "text.primary" }}>
          <BarChart
            dataset={chartData.length ? chartData : [{ amount: 0, month: "" }]}
            series={[
              {
                type: "bar",
                dataKey: "amount",
                color: AMOUNT_COLOR,
                valueFormatter: (value) => formatAmountInPounds(value),
              },
            ]}
            xAxis={[{ scaleType: "band", dataKey: "month", categoryGapRatio: 0.4 }]}
            yAxis={[{ scaleType: "linear" }]}
            tooltipTitleProperty="monthYear"
            // Weight shown on each bar — hidden when bars are too narrow to fit
            // it (still available on hover).
            barLabel={
              showBarLabels
                ? (item) => formatAmountInPounds(typeof item.value === "number" ? item.value : 0)
                : undefined
            }
            barLabelColor="rgba(0, 0, 0, 0.75)"
            margin={{ top: 30, bottom: 25 }}
          />
        </Box>
        <Stack
          direction="row"
          spacing={3}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 3.5, pt: 2, borderTop: 1, borderColor: "divider" }}
        >
          <Stat label="Amount" value={formatAmountInPounds(maxAmount) || "—"} />
          <Stat label="Months shipped" value={String(summary.activeCount)} />
          <Stat
            label="Peak month"
            value={
              summary.peak
                ? `${summary.peak.monthYear} · ${formatAmountInPounds(summary.peak.amount)}`
                : "—"
            }
          />
          <Stat
            label="Avg / month shipped"
            value={summary.activeCount > 0 ? formatAmountInPounds(summary.avg) || "—" : "—"}
          />
        </Stack>
      </DashboardTile.ContentArea>
    </Stack>
  );
};
